import { HttpErrorResponse } from "@angular/common/http";

export interface ProgramPackResolutionRequest {
  configurationCode: string;
  jurisdictionCode?: string;
  programCode?: string;
  asOfUtc?: string | Date;
}

export interface ProgramPackSelection {
  sourceCode: string;
  jurisdictionCode?: string | null;
  programCode?: string | null;
}

export interface ProgramPackScope {
  scopeTypeCode: string;
  scopeKey?: string | null;
}

export interface ProgramPackConfigurationVersion {
  configurationVersionId: number;
  configurationCode: string;
  versionCode: string;
  displayName?: string | null;
  matchedScopeTypeCode: string;
  matchedScopeKey?: string | null;
  effectiveStartUtc: string;
  effectiveEndUtc?: string | null;
  cacheRevisionToken: string;
  matchedDimensions?: Record<string, string | null>;
}

export interface ProgramPackResolutionTraceEntry {
  scopeTypeCode: string;
  scopeKey?: string | null;
  precedenceRank: number;
  matchedBindingCount: number;
}

export interface ProgramPackExemption {
  exemptionCode: string;
  originalValue: string;
  exemptionReason?: string | null;
  scopeTypeCode?: string | null;
  scopeKey?: string | null;
}

export interface ProgramPackConfigurationResolution {
  disposition: string;
  reasonCode?: string | null;
  matchedBindingCount: number;
  version?: ProgramPackConfigurationVersion | null;
  exemption?: ProgramPackExemption | null;
  trace?: ProgramPackResolutionTraceEntry[];
}

export interface ProgramPackResolutionResult {
  resolution: ProgramPackConfigurationResolution;
  scopePrecedence: ProgramPackScope[];
  context: Record<string, string | null>;
  selection: ProgramPackSelection;
}

export interface ProgramPackContextEntry {
  key: string;
  value: string | null;
}

export interface ProgramPackWorkspaceResult<T> {
  state: ProgramPackWorkspaceState;
  data: T | null;
  error: ProgramPackWorkspaceDisplayError | null;
}

export type ProgramPackWorkspaceState = "resolved" | "invalid" | "missing" | "conflict" | "unavailable";

export interface ProgramPackWorkspaceDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
  reasonCode?: string;
  correlationId?: string | null;
}

export interface ProgramPackWorkspaceProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  reasonCode?: string;
  errors?: Record<string, string[] | string>;
}

export function toProgramPackSuccess<T>(data: T): ProgramPackWorkspaceResult<T> {
  return {
    state: "resolved",
    data,
    error: null,
  };
}

export function toProgramPackFailure<T>(error: unknown): ProgramPackWorkspaceResult<T> {
  const displayError = mapProgramPackWorkspaceError(error);

  return {
    state: toWorkspaceState(displayError.statusCode),
    data: null,
    error: displayError,
  };
}

export function mapProgramPackWorkspaceError(error: unknown): ProgramPackWorkspaceDisplayError {
  if (error instanceof HttpErrorResponse) {
    const problem = toProblemDetails(error.error);
    const statusCode = error.status || problem.status;
    const messages = new Set<string>();

    flattenValidationMessages(problem.errors).forEach((message) => messages.add(message));
    if (statusCode !== undefined && statusCode < 500 && normalizeText(problem.detail).length > 0) {
      messages.add(normalizeText(problem.detail));
    }

    if (messages.size === 0) {
      messages.add(getDefaultErrorMessage(statusCode));
    }

    return {
      title: statusCode !== undefined && statusCode >= 500
        ? getDefaultErrorTitle(statusCode)
        : normalizeText(problem.title) || getDefaultErrorTitle(statusCode),
      messages: [...messages],
      statusCode,
      reasonCode: normalizeText(problem.reasonCode) || undefined,
      correlationId: error.headers?.get("X-Correlation-ID"),
    };
  }

  return {
    title: "Unexpected error",
    messages: ["Program pack workspace could not be loaded. Try again or contact support if the problem persists."],
  };
}

function toWorkspaceState(statusCode?: number): ProgramPackWorkspaceState {
  switch (statusCode) {
    case 400:
      return "invalid";
    case 404:
      return "missing";
    case 409:
      return "conflict";
    default:
      return "unavailable";
  }
}

function toProblemDetails(errorBody: unknown): ProgramPackWorkspaceProblemDetails {
  if (!errorBody || typeof errorBody !== "object" || Array.isArray(errorBody)) {
    return {};
  }

  return errorBody as ProgramPackWorkspaceProblemDetails;
}

function flattenValidationMessages(errors: ProgramPackWorkspaceProblemDetails["errors"]): string[] {
  if (!errors || typeof errors !== "object") {
    return [];
  }

  return Object.values(errors).flatMap((entry) => {
    if (Array.isArray(entry)) {
      return entry
        .map((item) => normalizeText(item))
        .filter((item) => item.length > 0);
    }

    const normalized = normalizeText(entry);
    return normalized.length > 0 ? [normalized] : [];
  });
}

function getDefaultErrorTitle(statusCode?: number): string {
  switch (statusCode) {
    case 400:
      return "Request could not be completed";
    case 404:
      return "Program pack not found";
    case 409:
      return "Program pack conflict detected";
    default:
      return "Program pack workspace unavailable";
  }
}

function getDefaultErrorMessage(statusCode?: number): string {
  switch (statusCode) {
    case 400:
      return "Review the configuration code, jurisdiction, program, and date values, then try again.";
    case 404:
      return "No matching program pack was found for the supplied context.";
    case 409:
      return "Multiple program pack bindings matched the supplied context.";
    default:
      return "Program pack workspace could not be loaded. Try again or contact support if the problem persists.";
  }
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
